/**
 * Meta (Facebook) pixel + lead reporting.
 *
 * The pixel is loaded once in the root layout by <MetaPixel />, which also
 * fires PageView on every client-side route change. Events elsewhere go
 * through `trackMetaEvent`, a safe no-op when the pixel is off or blocked.
 *
 * The pixel only runs on the live production build. Staging deploys
 * (NEXT_PUBLIC_STAGING=1) and local dev stay silent so test visits never
 * reach the ads data; set NEXT_PUBLIC_META_PIXEL_ID to switch it on for a
 * local check.
 */
import { reportLeadFormConversion } from "@/lib/gtag";

const IS_LIVE_BUILD =
  process.env.NODE_ENV === "production" &&
  process.env.NEXT_PUBLIC_STAGING !== "1";

// Public by design: Meta prints the pixel id in every page's source.
export const META_PIXEL_ID =
  process.env.NEXT_PUBLIC_META_PIXEL_ID ||
  (IS_LIVE_BUILD ? "823223440867784" : "");

type FbqFn = (...args: unknown[]) => void;

function getFbq(): FbqFn | null {
  if (typeof window === "undefined") return null;
  const f = (window as unknown as { fbq?: FbqFn }).fbq;
  return typeof f === "function" ? f : null;
}

/**
 * Send a standard Meta event. The pixel script loads after the page is
 * interactive, so an event raised during the first render (ViewContent on
 * a project page) waits up to 5 s for it instead of being dropped.
 */
export function trackMetaEvent(
  event: string,
  params?: Record<string, unknown>,
  attempt = 0
): void {
  if (!META_PIXEL_ID || typeof window === "undefined") return;
  const fbq = getFbq();
  if (fbq) {
    if (params) fbq("track", event, params);
    else fbq("track", event);
    return;
  }
  if (attempt < 20) {
    window.setTimeout(() => trackMetaEvent(event, params, attempt + 1), 250);
  }
}

/**
 * Report one successful lead to every ad platform: the Google Ads
 * "Submit lead form" conversion and the Meta Lead event. Call ONLY after
 * the backend has accepted the form, never on page load.
 */
export function reportLead(formName: string): void {
  reportLeadFormConversion();
  trackMetaEvent("Lead", { content_name: formName });
}
